import { Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';

const DEFAULT_ALLOWED_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

interface FileDropZoneProps {
  file: File | null;
  onChange: (file: File | null) => void;
  onError?: (error: string) => void;
  accept?: string;
  allowedMime?: string[];
  maxBytes?: number;
  placeholder?: string;
  hint?: string;
}

export function FileDropZone({
  file,
  onChange,
  onError,
  accept = '.pdf,.docx,.jpg,.jpeg,.png',
  allowedMime = DEFAULT_ALLOWED_MIME,
  maxBytes = DEFAULT_MAX_BYTES,
  placeholder = 'Drag & drop or click to select',
  hint,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function validate(f: File): string | null {
    if (!allowedMime.includes(f.type)) return 'File type not allowed.';
    if (f.size > maxBytes) return `File exceeds ${formatBytes(maxBytes)} limit.`;
    return null;
  }

  function handleFile(f: File) {
    const err = validate(f);
    if (err) {
      onError?.(err);
      return;
    }
    onError?.('');
    onChange(f);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  return (
    <div
      className={[
        'relative cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
        dragging ? 'border-brand-400 bg-brand-25' : 'border-gray-200 hover:border-brand-400',
      ].join(' ')}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      {file ? (
        <div className="flex items-center justify-center gap-2">
          <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
          <span className="text-xs text-gray-400 shrink-0">({formatBytes(file.size)})</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              onError?.('');
            }}
            className="shrink-0 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1.5">
          <Upload className="h-5 w-5 text-gray-400" />
          <p className="text-sm text-gray-400">{placeholder}</p>
          {hint && <p className="text-xs text-gray-300">{hint}</p>}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={handleInputChange}
      />
    </div>
  );
}
